/**
 * React Query hook for fetching and aggregating import job status.
 *
 * Hits the provider-agnostic `/api/orgs/<orgId>/import-jobs/` endpoint which
 * groups jobs by provider and inlines provider display metadata
 * (`display_name`, `kind`).  No assumption is made about which providers exist
 * — Gmail, Google Calendar, the dev/QA test integration, and any future
 * provider all flow through the same code path.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';

import { fetchWithDefaults } from '@/lib/http/client';
import {
  type ImportProviderName,
  PROVIDER_DISPLAY_REGISTRY,
} from '@/lib/integrations/providerDisplayRegistry';
import { createLogger } from '@/lib/logger';

const logger = createLogger({ module: 'useImportStatus' });

// Status values match backend ImportJob.Status enum:
// 'active' = ImportJob.Status.ACTIVE
// 'completed' = ImportJob.Status.COMPLETED
// 'failed' = ImportJob.Status.FAILED
// Source: apps/vendor_imports/models.py ImportJob.Status
export type ImportJobStatus = 'active' | 'completed' | 'failed';

export type ImportProviderKind = 'email' | 'calendar' | 'crm';

export interface ImportJobProgress {
  items_processed: number;
  items_failed: number;
  total_estimated: number | null;
  percent_complete: number;
  estimated_completion_at: string | null;
}

export interface ImportJob {
  id: number;
  job_type: string;
  status: ImportJobStatus;
  progress: ImportJobProgress;
  is_backing_off: boolean;
  backoff_until: string | null;
  backoff_seconds_remaining: number | null;
  last_error: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  social_account_id: number;
}

// Zod schemas for runtime validation
const ImportJobProgressSchema = z.object({
  items_processed: z.number().int().nonnegative(),
  items_failed: z.number().int().nonnegative(),
  total_estimated: z.number().int().nonnegative().nullable(),
  percent_complete: z.number().min(0).max(100),
  estimated_completion_at: z.string().nullable(),
});

const ImportJobSchema = z.object({
  id: z.number(),
  job_type: z.string(),
  status: z.enum(['active', 'completed', 'failed']),
  progress: ImportJobProgressSchema,
  is_backing_off: z.boolean(),
  backoff_until: z.string().nullable(),
  backoff_seconds_remaining: z.number().nullable(),
  last_error: z.string(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  social_account_id: z.number(),
});

const ImportProviderServiceSchema = z.object({
  provider_name: z.string(),
  display_name: z.string(),
  kind: z.enum(['email', 'calendar', 'crm']),
  jobs: z.array(ImportJobSchema),
});

const ImportJobsResponseSchema = z.object({
  services: z.array(ImportProviderServiceSchema),
});

/**
 * UI-ready view of a single provider's import status.
 *
 * Combines backend-provided metadata (provider_name, display_name, kind) with
 * frontend display assets (iconPath via PROVIDER_DISPLAY_REGISTRY) and derived
 * UI counters (progress, eta, active).
 */
export interface ServiceImportStatus {
  /** Stable identifier from the backend (e.g., "gmail", "testemail"). */
  providerName: string;
  /** Human-readable label (e.g., "Gmail", "Test Email"). */
  displayName: string;
  /** Semantic category for UI grouping/ordering. */
  kind: ImportProviderKind;
  /**
   * Frontend asset path for the provider icon, or `null` when the provider
   * has no registered icon (consumers should render a neutral placeholder).
   */
  iconPath: string | null;
  /** Progress percentage 0-100. Capped at 99 while jobs are active. */
  progress: number;
  /**
   * True while the provider is actively importing but NO active job carries a
   * usable total estimate (e.g. calendar imports report no total up front).
   * `progress` is meaningless in this state — render an indeterminate bar
   * instead of a frozen 0%.
   */
  indeterminate: boolean;
  /** Total items successfully imported across this provider's jobs. */
  itemsProcessed: number;
  /** Total items this provider's jobs reported as failed/skipped. */
  itemsFailed: number;
  /** True while any job is in 'active' status. */
  active: boolean;
  /** True if this provider has any jobs at all (active OR terminal). */
  hasJobs: boolean;
  /** Human-readable ETA string ("3m remaining"), null when not applicable. */
  estimatedTimeRemaining: string | null;
  /** Raw jobs for callers that need per-job details. */
  jobs: ImportJob[];
}

/** Kinds the recent-items ticker can actually render. 'crm' is excluded: the
 * ticker only shows email subjects and meeting titles, so a live CRM import
 * must not enter the active-kind set — otherwise it would enable useless
 * timeline polling in useRecentImportItems that can never produce an item. */
const TICKER_RENDERABLE_KINDS: ReadonlySet<ImportProviderKind> = new Set([
  'email',
  'calendar',
]);

/**
 * Kinds ('email' / 'calendar') that currently have an actively-importing
 * provider. Sorted for stable query-key composition. Used to scope the
 * recent-items ticker to what is genuinely arriving right now — a finished
 * email import must not keep feeding email subjects into the ticker while
 * only the calendar import is still running. 'crm' is intentionally omitted
 * (see TICKER_RENDERABLE_KINDS).
 */
export function getActiveImportKinds(
  services: ServiceImportStatus[]
): ImportProviderKind[] {
  const kinds = new Set<ImportProviderKind>();
  for (const service of services) {
    if (service.active && TICKER_RENDERABLE_KINDS.has(service.kind)) {
      kinds.add(service.kind);
    }
  }
  return [...kinds].sort();
}

/**
 * Build the compact count label for a provider row, e.g. "312 imported" or
 * "194 imported · 45 skipped". Returns null before anything has been imported
 * so a just-started row shows only its label + activity dot.
 *
 * Shared by every surface that renders per-provider import progress
 * (OnboardingImportState on the actions page, ImportStatusSection on the
 * dashboard) so the wording can never drift between them.
 */
export function buildImportCountLabel(
  service: ServiceImportStatus
): string | null {
  if (service.itemsProcessed <= 0 && service.itemsFailed <= 0) {
    return null;
  }
  const imported = `${service.itemsProcessed.toLocaleString()} imported`;
  if (service.itemsFailed > 0) {
    return `${imported} · ${service.itemsFailed.toLocaleString()} skipped`;
  }
  return imported;
}

export interface ImportStatus {
  hasActiveImports: boolean;
  combinedProgress: number;
  totalEmailsProcessed: number;
  /**
   * Total meetings synced across this user's calendar integrations, summed from
   * each calendar provider's ImportJob.items_processed. Scoped to the current
   * user (the /import-jobs/ endpoint filters by request.user), NOT org-wide.
   */
  totalMeetingsProcessed: number;
  estimatedTimeRemaining: string | null;
  /**
   * One entry per provider that has at least one ImportJob.  Ordered:
   * email kind first, then calendar kind.  Within a kind, the backend
   * orders by display_name for stable rendering.
   */
  services: ServiceImportStatus[];
  jobs: ImportJob[];
  isLoading: boolean;
  error: string | null;
}

interface UseImportStatusOptions {
  orgId: string;
  /**
   * Narrow the result to a single connected account.
   *
   * Omit it (the default) for aggregate surfaces — dashboard, calendar,
   * proposal center — where "everything this user is importing in this org" is
   * the right answer. Those callers share one query key and therefore one poll.
   *
   * Pass it from any surface that renders one card per connected account.
   * Grouping is by *provider*, so two Google accounts arrive in a single
   * `gmail` service entry; without this every card renders whichever job
   * happens to have the worst ETA, which is how three accounts came to show
   * one account's import (#10435).
   */
  socialAccountId?: number;
}

interface FetchedService {
  providerName: string;
  displayName: string;
  kind: ImportProviderKind;
  jobs: ImportJob[];
}

interface FetchResult {
  services: FetchedService[];
  error: string | null;
}

// Stable references for empty / loading / error states. Declared at module
// scope so consumers depending on hook output stay referentially stable when
// the hook short-circuits.
const EMPTY_STATUS: ImportStatus = {
  hasActiveImports: false,
  combinedProgress: 0,
  totalEmailsProcessed: 0,
  totalMeetingsProcessed: 0,
  estimatedTimeRemaining: null,
  services: [],
  jobs: [],
  isLoading: false,
  error: null,
};

// Stable reference returned while orgId is missing or the initial fetch is in flight.
const LOADING_STATUS: ImportStatus = { ...EMPTY_STATUS, isLoading: true };

// Stable reference returned when the underlying query has errored and no
// prior data is available to fall back on. NOTE: with the current
// `fetchImportJobs` implementation, errors are caught and surfaced via the
// success-branch's `error` field, so this short-circuit is defensive only.
const ERROR_STATUS: ImportStatus = {
  ...EMPTY_STATUS,
  error: 'Unable to load import status',
};

/**
 * Calculate estimated time remaining from the jobs with the furthest ETA.
 *
 * Backend Contract (ImportJobSerializer.get_progress):
 * - estimated_completion_at already includes backoff time when job is backing off
 * - ETA calculation: now + (remaining / rate) + backoff_seconds_remaining
 * - This coupling is validated via "ETA/backoff contract" tests
 * - See: apps/vendor_imports/serializers.py ImportJobSerializer.get_progress()
 */
function calculateTimeRemaining(jobs: ImportJob[]): string | null {
  const activeJobs = jobs.filter(j => j.status === 'active');
  if (activeJobs.length === 0) {
    return null;
  }

  // Find the max ETA (latest completion time)
  let maxEta: Date | null = null;
  for (const job of activeJobs) {
    if (job.progress.estimated_completion_at) {
      const eta = new Date(job.progress.estimated_completion_at);
      if (!maxEta || eta > maxEta) {
        maxEta = eta;
      }
    }
  }

  if (!maxEta) {
    return null;
  }

  const now = new Date();
  const remainingMs = maxEta.getTime() - now.getTime();

  // Handle negative/past ETA
  if (remainingMs <= 0) {
    return 'Almost done…';
  }

  const remainingSeconds = Math.ceil(remainingMs / 1000);
  if (remainingSeconds < 60) {
    return `${remainingSeconds}s remaining`;
  }

  const remainingMinutes = Math.ceil(remainingSeconds / 60);
  if (remainingMinutes < 60) {
    return `${remainingMinutes}m remaining`;
  }

  const remainingHours = Math.floor(remainingMinutes / 60);
  const extraMinutes = remainingMinutes % 60;
  if (extraMinutes === 0) {
    return `${remainingHours}h remaining`;
  }
  return `${remainingHours}h ${extraMinutes}m remaining`;
}

/**
 * Calculate progress with capping logic.
 * Handles: cap at 99 while active, no-estimate edge case, all-failed edge case.
 */
export function calculateProgressWithCap(
  processed: number,
  total: number,
  hasActive: boolean,
  hasCompleted: boolean
): number {
  // If no active jobs and some completed, show 100% (import is done)
  if (!hasActive && hasCompleted) {
    return 100;
  }

  if (total === 0) {
    // No estimates available
    return 0;
  }

  const progress = Math.round((processed / total) * 100);

  // Cap at 99 while jobs are active
  if (hasActive) {
    return Math.min(progress, 99);
  }

  return Math.min(progress, 100);
}

/**
 * Calculate progress for a set of jobs (single provider or combined).
 * Aggregates counts and applies capping logic.
 */
export function calculateServiceProgress(jobs: ImportJob[]): number {
  if (jobs.length === 0) {
    return 0;
  }

  const hasActiveJobs = jobs.some(j => j.status === 'active');
  const hasCompletedJobs = jobs.some(j => j.status === 'completed');
  const allJobsFailed = jobs.every(j => j.status === 'failed');

  // If all jobs failed, don't show 100% progress
  if (allJobsFailed) {
    return 0;
  }

  // Only count jobs that have estimates for progress calculation
  let totalProcessed = 0;
  let totalEstimated = 0;

  for (const job of jobs) {
    if (
      job.progress.total_estimated !== null &&
      job.progress.total_estimated > 0
    ) {
      totalProcessed += job.progress.items_processed;
      totalEstimated += job.progress.total_estimated;
    }
  }

  return calculateProgressWithCap(
    totalProcessed,
    totalEstimated,
    hasActiveJobs,
    hasCompletedJobs
  );
}

/**
 * Sum `items_processed` across the given jobs, skipping (and warning on) any
 * job whose count is not a finite non-negative number. Shared by the email and
 * calendar totals so both apply identical validation.
 */
function sumValidItemsProcessed(jobs: ImportJob[], orgId: string): number {
  return jobs.reduce((sum, job) => {
    const itemsProcessed = job.progress.items_processed;
    if (
      typeof itemsProcessed !== 'number' ||
      itemsProcessed < 0 ||
      !Number.isFinite(itemsProcessed)
    ) {
      logger.warn('Invalid items_processed value in import job', {
        jobId: job.id,
        jobType: job.job_type,
        itemsProcessed,
        orgId,
      });
      return sum;
    }
    return sum + itemsProcessed;
  }, 0);
}

/**
 * Sum `items_failed` across the given jobs, skipping (and warning on) any
 * job whose count is not a finite non-negative number. Mirrors
 * sumValidItemsProcessed so the failed-count aggregation gets the same
 * defensive guard as the processed-count aggregation.
 */
function sumValidItemsFailed(jobs: ImportJob[], orgId: string): number {
  return jobs.reduce((sum, job) => {
    const itemsFailed = job.progress.items_failed;
    if (
      typeof itemsFailed !== 'number' ||
      itemsFailed < 0 ||
      !Number.isFinite(itemsFailed)
    ) {
      logger.warn('Invalid items_failed value in import job', {
        jobId: job.id,
        jobType: job.job_type,
        itemsFailed,
        orgId,
      });
      return sum;
    }
    return sum + itemsFailed;
  }, 0);
}

/**
 * Lookup the frontend display metadata for a provider.  Returns `null` when
 * the provider has no registered icon — callers should render a neutral
 * placeholder rather than a vendor logo, so unknown providers are not
 * mislabeled as another (e.g. the test integration).
 */
function resolveProviderIcon(providerName: string): string | null {
  const known = PROVIDER_DISPLAY_REGISTRY[providerName as ImportProviderName];
  if (known) {
    return known.iconPath;
  }
  // Unknown provider — log so we notice but keep rendering with no icon.
  logger.warn(
    'useImportStatus: provider has no display registry entry, rendering without an icon',
    {
      providerName,
    }
  );
  return PROVIDER_DISPLAY_REGISTRY.__fallback.iconPath;
}

/**
 * Build the query key for an import-status fetch.
 *
 * Org-wide fetches keep the bare `['import-status', orgId]` key they have
 * always used, so the aggregate callers continue to share a single cache entry
 * and a single poll. An account-scoped fetch appends the account id, giving it
 * its own entry while remaining a prefix match — `invalidateQueries` on
 * `['import-status', orgId]` still reaches every scoped entry.
 */
function importStatusQueryKey(
  orgId: string,
  socialAccountId: number | undefined
): (string | number)[] {
  return socialAccountId === undefined
    ? ['import-status', orgId]
    : ['import-status', orgId, socialAccountId];
}

/** Fetch import jobs from the unified backend endpoint. */
async function fetchImportJobs(
  orgId: string,
  socialAccountId?: number
): Promise<FetchResult> {
  // `endpoint` is the query-free path and is the only form that reaches the
  // logger — logging a URL's query string is banned outright, so the scoping
  // account travels as its own structured field instead.
  const endpoint = `/api/orgs/${orgId}/import-jobs/`;
  const url =
    socialAccountId === undefined
      ? endpoint
      : `${endpoint}?social_account_id=${socialAccountId}`;

  // 5-second timeout protection to prevent indefinite hangs during server outages
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  let response: Response;
  try {
    response = await fetchWithDefaults(url, { signal: controller.signal });
  } catch (error) {
    const isTimeout = (error as Error)?.name === 'AbortError';
    logger.error(
      'Failed to fetch import jobs from unified endpoint',
      error as Error,
      {
        orgId,
        endpoint,
        error_type: isTimeout ? 'timeout' : 'network_error',
        timeout_ms: 5000,
      }
    );
    return { services: [], error: 'Unable to load import status' };
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    logger.error('Import jobs request returned non-2xx', undefined, {
      status: response.status,
      orgId,
      endpoint,
    });
    return { services: [], error: 'Unable to load import status' };
  }

  try {
    const raw = await response.json();
    const validation = ImportJobsResponseSchema.safeParse(raw);
    if (!validation.success) {
      logger.error('Import jobs response failed schema validation', undefined, {
        orgId,
        endpoint,
        validation_errors: validation.error.errors,
      });
      return { services: [], error: 'Unable to load import status' };
    }
    const services: FetchedService[] = validation.data.services.map(svc => ({
      providerName: svc.provider_name,
      displayName: svc.display_name,
      kind: svc.kind,
      jobs: svc.jobs,
    }));
    return { services, error: null };
  } catch (error) {
    logger.error('Failed to parse import jobs response', error as Error, {
      orgId,
      endpoint,
    });
    return { services: [], error: 'Unable to load import status' };
  }
}

/**
 * Hook for fetching and aggregating import status across all providers.
 */
export function useImportStatus(options: UseImportStatusOptions): ImportStatus {
  const { orgId, socialAccountId } = options;

  const enabled = orgId !== '' && orgId !== undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: importStatusQueryKey(orgId, socialAccountId),
    queryFn: () => fetchImportJobs(orgId, socialAccountId),
    enabled,
    staleTime: 2000,
    refetchInterval: query => {
      const result = query.state.data;
      if (!result) {
        return 5000;
      }

      // Continue polling on transient errors so the user recovers automatically.
      if (result.error) {
        return 5000;
      }

      // Defensive: if some other code path seeded the cache with a malformed
      // entry (missing `services`), don't crash — treat as "no active jobs".
      const { services } = result;
      if (!Array.isArray(services)) {
        return 5000;
      }

      const hasActiveJobs = services.some(svc =>
        svc.jobs.some(j => j.status === 'active')
      );

      return hasActiveJobs ? 5000 : false;
    },
  });

  return useMemo<ImportStatus>(() => {
    // Guard: return loading state when orgId is missing or invalid.
    if (!enabled) {
      return LOADING_STATUS;
    }

    if (isLoading && !data) {
      return LOADING_STATUS;
    }

    if (error && !data) {
      return ERROR_STATUS;
    }

    const fetchedServices: FetchedService[] = data?.services ?? [];
    const allJobs: ImportJob[] = fetchedServices.flatMap(svc => svc.jobs);
    const hasActiveImports = allJobs.some(j => j.status === 'active');

    const services: ServiceImportStatus[] = fetchedServices.map(svc => {
      const active = svc.jobs.some(j => j.status === 'active');
      const activeJobs = svc.jobs.filter(j => j.status === 'active');
      return {
        providerName: svc.providerName,
        displayName: svc.displayName,
        kind: svc.kind,
        iconPath: resolveProviderIcon(svc.providerName),
        progress: calculateServiceProgress(svc.jobs),
        indeterminate:
          active &&
          activeJobs.every(
            j =>
              j.progress.total_estimated === null ||
              j.progress.total_estimated <= 0
          ),
        itemsProcessed: sumValidItemsProcessed(svc.jobs, orgId),
        itemsFailed: sumValidItemsFailed(svc.jobs, orgId),
        active,
        hasJobs: svc.jobs.length > 0,
        estimatedTimeRemaining: calculateTimeRemaining(svc.jobs),
        jobs: svc.jobs,
      };
    });

    const combinedProgress = calculateServiceProgress(allJobs);

    // NOTE: sumValidItemsProcessed calls logger.warn on bad data, a side effect
    // inside useMemo. React requires useMemo callbacks to be pure; in dev under
    // React 18 StrictMode this warning may double-fire. Accepted trade-off: the
    // warning surfaces a real data-integrity problem worth seeing twice in dev.
    const emailJobs = services
      .filter(s => s.kind === 'email')
      .flatMap(s => s.jobs);
    const calendarJobs = services
      .filter(s => s.kind === 'calendar')
      .flatMap(s => s.jobs);
    const totalEmailsProcessed = sumValidItemsProcessed(emailJobs, orgId);
    const totalMeetingsProcessed = sumValidItemsProcessed(calendarJobs, orgId);

    return {
      hasActiveImports,
      combinedProgress,
      totalEmailsProcessed,
      totalMeetingsProcessed,
      estimatedTimeRemaining: calculateTimeRemaining(allJobs),
      services,
      jobs: allJobs,
      isLoading: false,
      error: data?.error ?? null,
    };
  }, [enabled, data, error, isLoading, orgId]);
}
